import { useCallback, useState } from 'react';
import { Resizable } from 'react-resizable';
import type { ResizeCallbackData } from 'react-resizable';
import type { TableProps } from 'antd';
import './resizable.css';

type AntColumns<T> = NonNullable<TableProps<T>['columns']>;
type AntColumn<T> = AntColumns<T>[number];

/** Extra props our header-cell override injects via `onHeaderCell`. */
interface ResizableTitleProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  onResize?: (e: React.SyntheticEvent, data: ResizeCallbackData) => void;
  width?: number;
}

/** Header cell that renders a drag handle on its right edge. */
function ResizableTitle(props: ResizableTitleProps) {
  const { onResize, width, ...rest } = props;
  if (!width || !onResize) {
    // No fixed width → not resizable; render a plain header cell.
    return <th {...rest} />;
  }
  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: -5,
            bottom: 0,
            top: 0,
            width: 10,
            cursor: 'col-resize',
            zIndex: 1,
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...rest} style={{ ...rest.style, position: 'relative' }} />
    </Resizable>
  );
}

function keyOf<T>(col: AntColumn<T>, index: number): string {
  const c = col as { key?: React.Key; dataIndex?: React.Key };
  return String(c.key ?? c.dataIndex ?? `col-${index}`);
}

function loadWidths(storageKey: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(`viq-admin.colWidths.${storageKey}`);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/**
 * Returns columns + components to spread into an AntD <Table> so its
 * columns become user-resizable, with widths persisted to localStorage
 * under `storageKey`. Only columns that have a numeric `width` (default
 * or persisted) get a drag handle.
 */
export function useResizableColumns<T>(
  storageKey: string,
  baseColumns: AntColumns<T>,
): { columns: AntColumns<T>; components: TableProps<T>['components'] } {
  const [widths, setWidths] = useState<Record<string, number>>(() =>
    loadWidths(storageKey),
  );

  const persist = useCallback(
    (next: Record<string, number>) => {
      setWidths(next);
      try {
        localStorage.setItem(
          `viq-admin.colWidths.${storageKey}`,
          JSON.stringify(next),
        );
      } catch {
        /* quota / disabled — non-fatal */
      }
    },
    [storageKey],
  );

  const handleResize = useCallback(
    (colKey: string) =>
      (_e: React.SyntheticEvent, data: ResizeCallbackData) => {
        const w = Math.max(60, Math.round(data.size.width)); // min 60px
        persist({ ...widths, [colKey]: w });
      },
    [persist, widths],
  );

  const columns: AntColumns<T> = baseColumns.map((col, index) => {
    const k = keyOf(col, index);
    const baseWidth = (col as { width?: number }).width;
    const width =
      widths[k] ?? (typeof baseWidth === 'number' ? baseWidth : undefined);
    return {
      ...col,
      width,
      onHeaderCell: (column) => {
        const colWidth = (column as { width?: number }).width;
        const headerProps: ResizableTitleProps = {
          width: typeof colWidth === 'number' ? colWidth : undefined,
          onResize: handleResize(k),
        };
        return headerProps as React.HTMLAttributes<HTMLElement>;
      },
    } as AntColumn<T>;
  });

  return { columns, components: { header: { cell: ResizableTitle } } };
}
